/**
 * Infer Render Type
 *
 * Heuristics to detect a better render type for nodes that fell back to "object".
 * Pure function — no side effects or dependencies.
 */

/** Markdown detection patterns */
const MARKDOWN_PATTERNS: RegExp[] = [
	/^#{1,6}\s/m, // headings
	/^\s*[-*+]\s/m, // unordered lists
	/^\s*\d+\.\s/m, // ordered lists
	/\*\*[^*]+\*\*/, // bold
	/```[\s\S]*?```/, // code blocks
	/^\s*>/m, // blockquotes
	/\[.+\]\(.+\)/, // links
	/!\[.*\]\(.+\)/, // images
];

/** Image URL patterns */
const IMAGE_URL_PATTERN =
	/\.(png|jpg|jpeg|gif|webp|svg|bmp|ico|avif)(\?|$)|^data:image\/|rool\.dev\/media/i;

/**
 * Detect if text is markdown content.
 * Requires length >= 30 and at least 2 markdown patterns.
 */
function isMarkdownContent(text: string): boolean {
	if (text.length < 30) return false;
	let matches = 0;
	for (const pattern of MARKDOWN_PATTERNS) {
		if (pattern.test(text)) {
			matches++;
			if (matches >= 2) return true;
		}
	}
	return false;
}

function isImageUrl(url: string): boolean {
	return IMAGE_URL_PATTERN.test(url);
}

/**
 * Infer a better render type for nodes that fell back to "object".
 * Detects markdown content and image URLs so these nodes can be rendered
 * with the appropriate dedicated renderer instead of a key-value dump.
 *
 * Returns null if no better type can be inferred.
 */
export function inferRenderType(
	obj: Record<string, unknown>,
): string | null {
	// Check for image URLs in common fields
	const imageFields = ['url', 'src', 'image'] as const;
	for (const field of imageFields) {
		const val = obj[field];
		if (typeof val === 'string' && isImageUrl(val)) {
			return 'image';
		}
	}

	// Check for markdown content in text/content fields
	const textFields = ['text', 'content', 'body', 'description'] as const;
	for (const field of textFields) {
		const val = obj[field];
		if (typeof val === 'string' && isMarkdownContent(val)) {
			return 'markdown';
		}
	}

	return null;
}
